/**
 * An RFC 7807 problem response from the API.
 *
 * The API already writes its `title` and `detail` in plain Arabic and says what the user should do next
 * (NFR-USE-05), so the clients show those strings rather than mapping status codes to their own copy. A
 * client only substitutes its own message where there is no response at all — the request never left the
 * device — because the server cannot have written one.
 */
export interface ProblemDetails {
  type?: string;
  title?: string;
  detail?: string;
  status?: number;
  instance?: string;
  /** Set on every problem the API writes; quote it to support when reporting a fault. */
  traceId?: string;
  errors?: Record<string, string[]>;
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails;
  /** When the API said the caller may ask again (`Retry-After`, as epoch milliseconds); set on a 429 (NR-2). */
  readonly retryAt: number | null;

  constructor(status: number, problem: ProblemDetails, retryAt: number | null = null) {
    super(problem.title ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.retryAt = retryAt;
  }

  /** The problem `type` slug, e.g. `locked-out` for `https://daghfal.app/problems/locked-out`. */
  get kind(): string | null {
    const type = this.problem.type;
    if (!type) return null;
    const slug = type.split('/').pop();
    return slug && slug !== 'about:blank' ? slug : null;
  }

  /** Per-field validation messages, flattened for display next to an input. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [field, messages] of Object.entries(this.problem.errors ?? {})) {
      // The key arrives in the DTO's casing (`NameAr`); inputs are named in camelCase.
      const key = field.charAt(0).toLowerCase() + field.slice(1);
      if (messages[0]) out[key] = messages.join(' ');
    }
    return out;
  }
}

/**
 * When a `Retry-After` header says the caller may ask again, as epoch milliseconds: the API sends seconds, counted from
 * `now`, and a proxy may send an HTTP date. Null when absent or unreadable.
 */
export function parseRetryAfter(value: string | null, now: number = Date.now()): number | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  if (/^\d+$/.test(trimmed)) return now + Number(trimmed) * 1000;
  const at = Date.parse(trimmed);
  return Number.isNaN(at) ? null : at;
}

/**
 * What is left of the wait a refusal asked for, in whole seconds, so a screen that mounts again over the same error
 * does not start the whole wait over. Null when the error is not a refusal that named one.
 */
export function retryAfterOf(error: unknown, now: number = Date.now()): number | null {
  if (!(error instanceof ApiError) || error.retryAt === null) return null;
  return Math.max(0, Math.ceil((error.retryAt - now) / 1000));
}

/** Thrown when the request never reached the API — offline, DNS, TLS, or a cancelled connection. */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('network-unreachable');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/**
 * Whether asking again could give a different answer.
 *
 * A dropped connection, a failure of ours (5xx), a timeout (408) and the rate limiter (429) can each clear on
 * their own. Any other 4xx is an answer about the request or the caller, and a second attempt gets the same one:
 * offering "Try again" under a refusal only suggests that the user did something wrong by not persisting.
 */
export function isRetryable(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  return error.status >= 500 || error.status === 408 || error.status === 429;
}
